import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { fetchUsers } from "../../../api/user";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../PMDialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

const DEFAULT_EMPLOYEE_EXTRA_FILTERS = [];

export default function ModernEmployeeSelector({
  open = false,
  selectedEmployees = [],
  onChange,
  onClose,
  onConfirm,
  multiple = false,
  filters = DEFAULT_EMPLOYEE_EXTRA_FILTERS,
  showHeader = true,
  useDialog = false,
  closeOnSelect,
  zIndex,
  closeOnOutsideClick,
  includeHired = false,
}) {
  const { t } = useTranslation();
  const shouldCloseOnSelect = closeOnSelect ?? !multiple;
  const dialogZIndex = zIndex ?? 2300;
  const allowOutsideClose = closeOnOutsideClick ?? false;
  const [searchQuery, setSearchQuery] = useState("");
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set(selectedEmployees.map((e) => e.id)));
  const [draftEmployees, setDraftEmployees] = useState(selectedEmployees);
  const searchInputRef = useAutofocusOnOpen(open);

  const perPage = 20;

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedEmployees.map((e) => e.id)));
      setDraftEmployees(selectedEmployees);
    }
  }, [selectedEmployees, open]);

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      if (selectedEmployees.length === 0) {
        setEmployees([]);
      } else {
        setEmployees(selectedEmployees);
      }
    } else {
      setEmployees([]);
    }
  }, [open, selectedEmployees]);

  const searchFunction = useCallback(async (query, page, currentFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        page,
        per_page: perPage,
        sortBy: "id",
        sort: "desc",
        fields: "id,name,displayTitle,email,user_type_id",
        showInDropdown: true,
        filters: [
          { name: includeHired ? "EMPLOYEE_OR_HIRED" : "EMPLOYEE", value: true },
          ...currentFilters,
        ],
      };

      if (query && query.trim()) {
        params.query = query.trim();
      }

      const response = await fetchUsers(params);

      if (response && response.data) {
        if (page === 1) {
          setEmployees(response.data);
        } else {
          setEmployees((prev) => [...prev, ...response.data]);
        }

        setHasMore(
          response.data.length === perPage &&
            page * perPage < (response.total_count || 0)
        );
      } else {
        setEmployees([]);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError(t("employee_selector.load_error", { defaultValue: "Kunne ikke laste ansatte" }));
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  }, [t, includeHired]);

  const debouncedSearchRef = useRef(debounce(searchFunction, 300));

  useEffect(() => {
    debouncedSearchRef.current.cancel();
    debouncedSearchRef.current = debounce(searchFunction, 300);
    return () => debouncedSearchRef.current.cancel();
  }, [searchFunction]);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (!open) {
      debouncedSearchRef.current.cancel();
      setIsLoading(false);
      return undefined;
    }

    setCurrentPage(1);
    debouncedSearchRef.current(searchQuery, 1, filtersRef.current);
    return () => debouncedSearchRef.current.cancel();
  }, [searchQuery, open, filters]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearchRef.current(searchQuery, nextPage, filtersRef.current);
    }
  }, [isLoading, hasMore, currentPage, searchQuery]);

  const buildSelection = useCallback(
    (idsSet, sourceEmployees) => {
      const selectedFromCurrent = sourceEmployees.filter((e) => idsSet.has(e.id));
      const selectedFromPrevious = draftEmployees.filter(
        (e) => idsSet.has(e.id) && !sourceEmployees.find((emp) => emp.id === e.id)
      );
      return [...selectedFromCurrent, ...selectedFromPrevious];
    },
    [draftEmployees]
  );

  const toggleEmployee = useCallback(
    (employee) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(employee.id)) {
        newSelectedIds.delete(employee.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
        }
        newSelectedIds.add(employee.id);
      }

      setSelectedIds(newSelectedIds);
      const selected = buildSelection(newSelectedIds, employees);
      setDraftEmployees(selected);

      if (shouldCloseOnSelect) {
        if (onChange) onChange(selected);
        if (!multiple && newSelectedIds.size > 0 && onClose) {
          setTimeout(() => onClose(), 200);
        }
      }
    },
    [selectedIds, employees, multiple, onChange, onClose, shouldCloseOnSelect, buildSelection]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setDraftEmployees([]);
  }, []);

  const handleConfirm = () => {
    const selected = buildSelection(selectedIds, employees);
    if (onChange) onChange(selected);
    if (onConfirm) onConfirm(selected);
    if (onClose) onClose();
  };

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open || !useDialog) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, useDialog, handleClose]);

  const isSelected = useCallback((employeeId) => selectedIds.has(employeeId), [selectedIds]);

  const panel = (
    <div className="flex h-full min-h-0 flex-col bg-white">
      {showHeader ? (
        <div className="flex-shrink-0 border-b border-gray-200 p-3 sm:p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">
              {multiple
                ? t("employee_selector.title_multiple", { defaultValue: "Velg ansatte" })
                : t("employee_selector.title_single", { defaultValue: "Velg ansatt" })}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 ? (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title={t("employee_selector.clear_all", { defaultValue: "Fjern alle valg" })}
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              ) : null}
              {!useDialog && onClose ? (
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-md p-1.5 text-gray-400 transition-colors hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              ) : null}
            </div>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("employee_selector.search_placeholder", { defaultValue: "Søk etter ansatte..." })}
              className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            ) : null}
          </div>

          {selectedIds.size > 0 ? (
            <div className="mt-2 text-sm text-gray-600">
              {t("employee_selector.selected_count", {
                count: selectedIds.size,
                defaultValue: `${selectedIds.size} valgt`,
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && employees.length === 0 ? (
          <div className="flex h-48 items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              <p className="text-sm text-gray-500">
                {t("employee_selector.loading", { defaultValue: "Laster ansatte..." })}
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : employees.length === 0 ? (
          <div className="flex h-48 items-center justify-center px-4 text-center">
            <p className="text-sm text-gray-500">
              {searchQuery
                ? t("employee_selector.no_results", { defaultValue: "Ingen ansatte funnet" })
                : t("employee_selector.empty_hint", { defaultValue: "Søk etter ansatte for å begynne" })}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {employees.map((employee) => {
              const selected = isSelected(employee.id);
              return (
                <div
                  key={employee.id}
                  className={`border-b border-gray-100 p-3 last:border-b-0 ${
                    selected ? "border-l-4 border-l-blue-600 bg-blue-50" : ""
                  } ${!multiple ? "cursor-pointer hover:bg-gray-50" : ""}`}
                  onClick={!multiple ? () => toggleEmployee(employee) : undefined}
                >
                  <div className="flex items-start space-x-3">
                    <div className="mt-0.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleEmployee(employee);
                        }}
                        className="focus:outline-none"
                      >
                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                            selected
                              ? "border-blue-600 bg-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {selected ? <CheckIcon className="h-4 w-4 text-white" /> : null}
                        </div>
                      </button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center truncate text-sm font-medium text-gray-900">
                        {employee.displayTitle || employee.name}
                        {employee.user_type_id === 3 ? (
                          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            {t("employeeSelector.hired_badge")}
                          </span>
                        ) : null}
                      </p>
                      {employee.email ? (
                        <p className="mt-0.5 text-xs text-gray-500">{employee.email}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasMore && !isLoading ? (
          <div className="border-t border-gray-200 p-3 text-center">
            <button
              type="button"
              onClick={loadMore}
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              {t("employee_selector.load_more", { defaultValue: "Last flere ansatte" })}
            </button>
          </div>
        ) : null}
      </div>

      {multiple && !shouldCloseOnSelect ? (
        <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("common.cancel", { defaultValue: "Avbryt" })}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            {t("employee_selector.confirm", { defaultValue: "Ferdig" })}
          </button>
        </div>
      ) : null}
    </div>
  );

  if (!open) return null;

  if (!useDialog) {
    return (
      <div className="h-full w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {panel}
      </div>
    );
  }

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      size="md"
      dense
      closeOnOutsideClick={allowOutsideClose}
      zIndex={dialogZIndex}
    >
      {panel}
    </PMDialog>
  );
}
